import { Portal } from 'react-portal';
import styled from 'styled-components';
import { border, space } from 'styled-system';

import { Box } from '@hivebrite/design-system/Box';
import type { BoxProps } from '@hivebrite/design-system/Box';
import { ButtonClose } from '@hivebrite/design-system/Button';
import { zIndices, useBreakpoint } from '@hivebrite/design-system/Theme';
import { useSize } from '@hivebrite/react-hooks';

import VisualViewport from 'hb-react/shared/components/VisualViewport';
import useFrameTheme from 'hb-react/shared/hooks/theme/useFrameTheme';

import type { ModalProps, ScrollWrapperProps } from './types';
import { useModal } from './useModal';

/**
  - Usage in FO: OK
  - Usage in PO: OK
  - Usage in BO: OK
 */

const Modal = ({
  alignItems = 'flex-start',
  center = false,
  children,
  customHeight,
  customZIndex,
  dataTestId = 'design-system-modal',
  enableEscAction = true,
  isFocusOnRef = true,
  onCloseModal,
  screenReadersCloseButtonText,
  viewportProps,
  withFullScreenModal = false,
  withTemplate = false,
}: ModalProps) => {
  const breakpoint = useBreakpoint();
  const frameTheme = useFrameTheme();

  const { labelledById, viewportRef, wrapperModalRef } = useModal({
    enableEscAction,
    isFocusOnRef,
    onCloseModal,
  });

  const viewportSize = useSize(viewportRef);

  return (
    <Portal>
      <ModalBackground
        alignItems={alignItems}
        aria-labelledby={labelledById}
        aria-modal="true"
        data-id="design-system-modal"
        data-testid={dataTestId}
        ref={wrapperModalRef}
        role="dialog"
        tabIndex={-1}
        zIndex={customZIndex}
      >
        <ScrollWrapper
          $backgroundColor={frameTheme.backgroundColor}
          $breakpoint={breakpoint}
          $center={center}
          $viewportProps={viewportProps}
          $withFullScreenModal={withFullScreenModal}
          $withTemplate={withTemplate}
          maxHeight={customHeight}
          ref={viewportRef}
          role="document"
          wrapScroll
        >
          {children({
            closeButton: (
              <IconCloseWrapper>
                <ButtonClose
                  dataTestId="close-ds-modal"
                  onClick={onCloseModal}
                  screenReadersText={screenReadersCloseButtonText}
                />
              </IconCloseWrapper>
            ),
            labelledById,
            viewportSize,
          })}
        </ScrollWrapper>
      </ModalBackground>
    </Portal>
  );
};

export default Modal;

const IconCloseWrapper = styled(Box).attrs(({ theme }) => ({
  position: 'absolute',
  right: `${theme.space[4]}px`,
  top: `${theme.space[4]}px`,
  zIndex: zIndices.modal + 1,
}))`
  svg {
    max-width: initial;
  }
`;

const ModalBackground = styled(Box).attrs<BoxProps>({
  bg: 'fog',
  bottom: 0,
  justifyContent: 'center',
  left: 0,
  position: 'fixed',
  right: 0,
  top: 0,
  width: '100%',
})<BoxProps>`
  z-index: ${({ zIndex = zIndices.modal }) => zIndex};
`;

const ScrollWrapper = styled(VisualViewport).attrs<ScrollWrapperProps>(
  ({ $center, $viewportProps, $withTemplate, theme }) => ({
    borderRadius: { _: 0, m: theme.themeVariables.globalBorderRadius },
    mb: $center ? 'auto' : '',
    mt:
      $withTemplate && $center
        ? 'auto'
        : $viewportProps?.mt ?? { _: 'inherit', m: 13 },
  }),
)<ScrollWrapperProps>`
  ${border}
  ${space}

  ${({ maxHeight }) => maxHeight && `max-height: ${maxHeight} !important;`}

  -webkit-overflow-scrolling: touch;
  overflow-y: auto;

  ${({ $breakpoint, $withTemplate }) =>
    $withTemplate &&
    $breakpoint === 's' &&
    `
      width: 100%;
      height: 100%;
  `};

  ${({ $backgroundColor, $breakpoint, $withFullScreenModal }) => {
    if ($withFullScreenModal && $breakpoint === 'm')
      return `
    background-color: ${$backgroundColor};
    width: 95%;
    height: 95%;
    `;
    if ($withFullScreenModal && $breakpoint === 's')
      return `
    background-color: ${$backgroundColor};
    width: 100%;
    height: 100%;
    `;
  }};
`;
